import React, { useEffect, useState, lazy } from 'react';
import { StarIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { useScrollReveal } from '../hooks/useScrollReveal';
export function TestimonialsSection() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const headerReveal = useScrollReveal({
    threshold: 0.2
  });
  const cardsReveal = useScrollReveal({
    threshold: 0.1
  });
  const testimonials = [{
    text: "I can get down on the floor with my grandkids again. That's not something I could say six months ago. This frankincense has given me my freedom back.",
    author: 'Sarah M., Tennessee',
    verified: true,
    rating: 5
  }, {
    text: "I was skeptical because nothing else worked — not turmeric, not collagen, nothing. But within two weeks, I noticed I wasn't wincing every time I stood up. Finally, something that actually does what it says.",
    author: 'Rebecca T., Georgia',
    verified: true,
    rating: 5
  }, {
    text: "As a Christian, I love that this is a biblical ingredient. It's not some trendy thing — it's been used for thousands of years. And it works. My knees feel 10 years younger.",
    author: 'Linda K., Texas',
    verified: true,
    rating: 5
  }, {
    text: 'I can finally kneel during prayer again without dreading it. That might sound small, but it means everything to me. Thank you, Faith & Fig.',
    author: 'Michelle D., North Carolina',
    verified: true,
    rating: 5
  }, {
    text: "The morning stiffness is almost gone. I used to need 20 minutes just to loosen up. Now I wake up and I can move. It's like my body remembers how it's supposed to feel.",
    author: 'Karen W., Ohio',
    verified: true,
    rating: 5
  }, {
    text: "I've tried so many joint supplements that did nothing. This one is different. I trust this brand because they're faith-based and they don't cut corners. My hips and hands feel so much better.",
    author: 'Deborah H., Florida',
    verified: true,
    rating: 5
  }, {
    text: "I was starting to feel like my body was giving up on me. Couldn't garden, couldn't keep up with my responsibilities. Two months on this frankincense and I'm back to being ME again.",
    author: 'Patricia L., Alabama',
    verified: true,
    rating: 5
  }, {
    text: "No fillers, no weird ingredients, just pure frankincense the way God made it. My joints feel supported and I'm not constantly aware of my body anymore. That's the best gift.",
    author: 'Jennifer R., Virginia',
    verified: true,
    rating: 5
  }, {
    text: "I was worried about taking something synthetic, so I prayed about it and found Faith & Fig. A biblical botanical from a Christian company — and it actually works. My shoulders and back haven't felt this good in years.",
    author: 'Angela S., Missouri',
    verified: true,
    rating: 5
  }];
  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  // Items per slide: 2 on mobile, 3 on desktop
  const itemsPerSlide = isMobile ? 2 : 3;
  const totalSlides = Math.ceil(testimonials.length / itemsPerSlide);
  // Auto-advance carousel
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % totalSlides);
    }, 5000);
    return () => clearInterval(timer);
  }, [totalSlides]);
  const nextSlide = () => {
    setCurrentSlide(prev => (prev + 1) % totalSlides);
  };
  const prevSlide = () => {
    setCurrentSlide(prev => (prev - 1 + totalSlides) % totalSlides);
  };
  const visibleTestimonials = testimonials.slice(currentSlide * itemsPerSlide, currentSlide * itemsPerSlide + itemsPerSlide);
  return <section className="w-full bg-[#2d4a3a] py-16 lg:py-24 relative overflow-hidden" data-id="element-480">
      {/* Animated Wave Background */}
      <div className="absolute inset-0 opacity-10" data-id="element-481">
        <svg className="w-full h-full" viewBox="0 0 1440 320" preserveAspectRatio="none" data-id="element-482">
          <path fill="currentColor" fillOpacity="1" d="M0,96L48,112C96,128,192,160,288,160C384,160,480,128,576,112C672,96,768,96,864,112C960,128,1056,160,1152,160C1248,160,1344,128,1392,112L1440,96L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z" data-id="element-483">
            <animate attributeName="d" dur="10s" repeatCount="indefinite" values="M0,96L48,112C96,128,192,160,288,160C384,160,480,128,576,112C672,96,768,96,864,112C960,128,1056,160,1152,160C1248,160,1344,128,1392,112L1440,96L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z;
                      M0,160L48,144C96,128,192,96,288,96C384,96,480,128,576,144C672,160,768,160,864,144C960,128,1056,96,1152,96C1248,96,1344,128,1392,144L1440,160L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z;
                      M0,96L48,112C96,128,192,160,288,160C384,160,480,128,576,112C672,96,768,96,864,112C960,128,1056,160,1152,160C1248,160,1344,128,1392,112L1440,96L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z" data-id="element-484" />
          </path>
        </svg>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10" data-id="element-485">
        {/* Section Header */}
        <div ref={headerReveal.ref} className={`text-center mb-12 transition-all duration-1000 ${headerReveal.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} data-id="element-486">
          <div className="flex items-center justify-center gap-2 mb-4" data-id="element-487">
            {[...Array(5)].map((_, i) => <StarIcon key={i} className="w-8 h-8 fill-[#d4af37] text-[#d4af37] animate-pulse" style={{
            animationDelay: `${i * 0.1}s`
          }} data-id="element-488" />)}
          </div>
          <h2 className="font-serif text-3xl lg:text-4xl font-bold text-white mb-4" data-id="element-489">
            Join 1,000,000+ Customers Who Love Faith & Fig
          </h2>
          <p className="text-white/80 text-lg" data-id="element-490">
            Real Christian women. Real results. Real comfort.
          </p>
        </div>

        {/* Testimonials Carousel */}
        <div className="relative" data-id="element-491">
          <div ref={cardsReveal.ref} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8" data-id="element-492">
            {visibleTestimonials.map((testimonial, index) => <div key={index} className={`bg-white rounded-2xl p-6 shadow-xl hover:shadow-2xl transition-all transform hover:-translate-y-1 ${cardsReveal.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`} style={{
            transitionDelay: `${index * 150}ms`,
            transitionDuration: '700ms'
          }} data-id="element-493">
                <div className="flex items-center gap-1 mb-4" data-id="element-494">
                  {[...Array(testimonial.rating)].map((_, i) => <StarIcon key={i} className="w-4 h-4 fill-[#d4af37] text-[#d4af37]" data-id="element-495" />)}
                </div>
                <p className="text-gray-700 leading-relaxed mb-4 text-sm" data-id="element-496">
                  "{testimonial.text}"
                </p>
                <div className="flex items-center justify-between" data-id="element-497">
                  <p className="font-semibold text-[#2d4a3a] text-sm" data-id="element-498">
                    {testimonial.author}
                  </p>
                  {testimonial.verified && <span className="text-xs text-[#496f58] font-medium" data-id="element-499">
                      ✓ Verified Customer
                    </span>}
                </div>
              </div>)}
          </div>

          {/* Carousel Controls */}
          <div className="flex items-center justify-center gap-4" data-id="element-500">
            <button onClick={prevSlide} className="w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 text-white flex items-center justify-center transition-all" aria-label="Previous testimonials" data-id="element-501">
              <ChevronLeftIcon className="w-5 h-5" data-id="element-502" />
            </button>

            {/* Dots Indicator */}
            <div className="flex gap-2" data-id="element-503">
              {Array.from({
              length: totalSlides
            }).map((_, index) => <button key={index} onClick={() => setCurrentSlide(index)} className={`w-2 h-2 rounded-full transition-all ${currentSlide === index ? 'bg-white w-8' : 'bg-white/40'}`} aria-label={`Go to slide ${index + 1}`} data-id="element-504" />)}
            </div>

            <button onClick={nextSlide} className="w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 text-white flex items-center justify-center transition-all" aria-label="Next testimonials" data-id="element-505">
              <ChevronRightIcon className="w-5 h-5" data-id="element-506" />
            </button>
          </div>
        </div>

        {/* Trust Badges */}
      </div>
    </section>;
}